<template>
  <div class="form-layout">
    <div class="login-form">
      <BaseElForm
        ref="formRef"
        class="login-form-container"
        label-position="top"
        :model="authFormData"
        :rules="formRules"
      >
        <img
          class="login-form__logo"
          :src="gimmatekLogo"
          :srcset="`${gimmatekLogo} 1x, ${gimmatekLogo2x} 2x`"
          alt="GIMMATEK"
        >
        <div class="flex justify-between">
          <h1 class="login-form__login">LOGIN</h1>
          <div v-if="showControl.langSwitcher" class="locale-changer">
            <el-select v-model="selectedLang">
              <el-option
                v-for="(lang, i) in langs"
                :key="`Lang${i}`"
                :label="lang.label"
                :value="lang.value"
              />
            </el-select>
          </div>
        </div>
        <BaseElFormItem prop="orgCode" :label="$t('login.group.title')">
          <OrgSearch data-testid="org-select" class="form__input__org" :model.sync="queryOrg" :disabled="!!subdomainCode" @change="changeOrg" />
          <img class="prefix__icon" :src="iconOrg" alt="organization icon">
        </BaseElFormItem>
        <BaseElFormItem prop="email" :label="$t('login.email.title')">
          <BaseElInput v-model="authFormData.email" data-testid="email-input" class="form__input__email" :placeholder="$t('login.email.placeholder')" @keyup.enter.native="handleLogin" />
          <img class="prefix__icon" :src="iconEmail" alt="email icon">
        </BaseElFormItem>
        <BaseElFormItem class="login-form__password w-full" prop="password" :label="$t('login.password.title')">
          <template #label>
            <div class="inline-flex justify-between w-full">
              <p>{{ $t('login.password.title') }}</p>
              <p class="underline cursor-pointer text-sub text-gray-60" @click="onForgetPassword">{{ $t('common.button.forgetPassword.text') }}</p>
            </div>
          </template>
          <BaseElInput
            v-model="authFormData.password"
            data-testid="password-input"
            class="form__input__password"
            show-password
            :placeholder="$t('login.password.placeholder')"
            @keyup.enter.native="handleLogin"
          />
          <img class="prefix__icon" :src="iconPassword" alt="password icon">
        </BaseElFormItem>
        <BaseElFormItem prop="agreeTerms" class="login-form__agreecheck">
          <BaseElCheckbox data-testid="agree-terms-checkbox" :value="authFormData.agreeTerms" @change="handleChangeTerms">
            {{ $t('login.agree.title') }}
            <span class="agree-terms" data-testid="show-terms-btn" @click.exact="handleClickTerms">《{{ $t('login.loginTerms.title') }}》。</span>
          </BaseElCheckbox>
        </BaseElFormItem>
        <BaseElFormItem>
          <BaseElButton
            data-testid="login-btn"
            type="primary"
            :loading="loading.login"
            @click="handleLogin"
          >
            {{ $t('login.button.login.text') }}
          </BaseElButton>
        </BaseElFormItem>
      </BaseElForm>
    </div>
    <p class="version">{{ $t('login.currentVersion.title') }}v{{ version }}</p>

    <GRecaptchaV2Modal
      v-if="showV2Recaptcha"
      @close="onCloseRecaptchaV2"
      @verify="v2RecaptchaVerify($event, handleLogin)"
    />
  </div>
</template>

<script>
import ls from '@/utils/localstorage'
import formUtils from '@/utils/form'
import OrgSearch from '@/components/Search/OrgSearch.vue'
import { EmailLogin, GetOrgsByCode } from '@/api/auth'
import { noEmptyRules, changeEventRules } from '@/validation'
import iconOrg from '@/assets/login/icon-org.svg'
import iconEmail from '@/assets/login/icon-email.svg'
import iconPassword from '@/assets/login/icon-password.svg'
import gimmatekLogo from '@/assets/header/logo-gimmatek.png'
import gimmatekLogo2x from '@/assets/header/logo-gimmatek-2x.png'
import { allowsUnknownHost, getAppVersion, isDevRuntime, resolveStoreOrg } from '@/utils/app'
import configuration from '@/configuration'
import { loadLanguageAsync, i18n } from '@/plugins/i18n/i18n'
import { ref, reactive, computed, onMounted, defineComponent } from 'vue'
import { useRouter } from 'vue-router/composables'
import store from '@/store'
import { useRecaptcha } from '@/use/recaptcha'
import { useNewsHint } from '@/use/useNewsHint'
import GRecaptchaV2Modal from '@/components/GRecaptchaV2Modal.vue'
import { get } from 'lodash'
import { useOrg } from '@/use/useOrg'

export default defineComponent({
  name: 'LoginForm',
  components: {
    OrgSearch,
    GRecaptchaV2Modal,
  },
  setup (props, { emit }) {
    const router = useRouter()
    const {
      recaptchaVerifyToken,
      v3RecaptchaVerify,
      recaptchaVersionFailed,
      recaptchaVersionUse,
      showV2Recaptcha,
      v2RecaptchaVerify,
      handleRecaptchaError,
    } = useRecaptcha()
    const { dashboardConfig } = useOrg()
    const formRef = ref(null)
    const subdomainCode = ref(null)
    const langs = ref([
      {
        label: '繁體中文',
        value: 'zh',
      },
      {
        label: 'English',
        value: 'en',
      },
      {
        label: '日本語',
        value: 'jp',
      },
      {
        label: '한국어',
        value: 'kr',
      },
    ])
    const authFormData = reactive({
      orgCode: '',
      email: '',
      password: '',
      agreeTerms: false,
    })
    const loading = reactive({
      login: false,
    })
    const orgList = ref([])
    const queryOrg = ref('')
    const selectedOrg = ref(null)
    const isClickTerms = ref(false)

    const selectedLang = computed({
      get: () => i18n.locale,
      set: (value) => {
      // 動態載入新語言
        loadLanguageAsync(value)
          .then(() => {
            console.log(`Language switched to: ${value}`)
          })
          .catch((error) => {
            console.error('Failed to switch language:', error)
          })
      },
    })
    const formRules = computed(() => ({
      email: noEmptyRules(i18n.t('login.email.noEmptyRules.text')),
      // password: noEmptyRules(i18n.t('login.password.placeholder')),
      password: [
        noEmptyRules(i18n.t('login.password.placeholder')),
        // minLengthRules(8, i18n.t('validation.minLength2.text', { number: 8 })),
        // atLeastOneUpperCaseRules(),
        // atLeastOneLowerCaseRules(),
        // atLeastOneNumberRules(),
        // atLeastOneSpecialCharRules(),
      ],
      orgCode: changeEventRules(i18n.t('login.group.changeEventRules.text')),
      agreeTerms: {
        message: i18n.t('login.agree.agreeRules.text'),
        trigger: 'change',
        validator: (rule, value, callback) => {
          if (value) return callback()
          return callback(new Error(i18n.t('login.agree.noEmptyRules.text')))
        },
      },
    }))

    const showControl = computed(() => {
      const controls = {
        langSwitcher: get(dashboardConfig.value, 'orgModulePermission.enableMultilingual'),
      }

      return controls
    })

    const version = computed(() => getAppVersion())
    // => 當改變集團
    const changeOrg = async () => {
      authFormData.orgCode = queryOrg.value.code
      await getOrgsInfoByCode()
    }

    // => 使用 code 取得集團資訊
    const getOrgsInfoByCode = async () => {
      try {
        selectedOrg.value = await GetOrgsByCode({ code: authFormData.orgCode })
        queryOrg.value = selectedOrg.value.name
        emit('change-selected-org', selectedOrg.value)
      } catch (error) {
        console.log(error)
      }
    }

    // => 依 orgCode 查詢並帶入表單，回傳是否成功取得集團
    const resolveOrgCode = async (code) => {
      subdomainCode.value = code
      authFormData.orgCode = code
      await getOrgsInfoByCode()
      // 查不到集團時還原 subdomainCode，避免集團欄位被鎖成無法選擇
      if (!selectedOrg.value) {
        subdomainCode.value = null
        return false
      }

      return true
    }

    // => 檢查子網域
    const checkSubDomain = async () => {
      const isDev = isDevRuntime()

      // 僅 dev 環境允許用 VUE_APP_DEV_ORG_CODE 覆寫，避免誤設時影響正式環境所有租戶
      const envOrgCode = isDev ? configuration('devOrgCode') : ''
      const hostOrg = resolveStoreOrg({ overrideCode: envOrgCode })

      if (hostOrg.kind === 'org') {
        if (!(await resolveOrgCode(hostOrg.code))) router.push('/not-found')

        return true
      }

      // 保留字、多層子網域、其他網域：找不到組織（dev／e2e 的 localhost 不算）
      if (hostOrg.kind === 'invalid' && !allowsUnknownHost()) {
        router.push('/not-found')

        return true
      }

      // dev 環境的預設集團：取得到就視同子網域鎖定，取不到則讓使用者自己選
      if (isDev) return resolveOrgCode('devorg')

      // 入口：不鎖定組織，使用者自己選
      return false
    }

    // => 點擊條款
    const handleClickTerms = (event) => {
      emit('showTerms')
      isClickTerms.value = true
    }

    // => 勾選同意條款
    const handleChangeTerms = () => {
      if (isClickTerms.value) {
        isClickTerms.value = false
        return
      }
      authFormData.agreeTerms = !authFormData.agreeTerms
    }

    // => 關閉 reCAPTCHA v2
    const onCloseRecaptchaV2 = () => {
      showV2Recaptcha.value = false
      loading.login = false
    }

    // => 登入
    const handleLogin = async () => {
      if (loading.login) return
      loading.login = true
      if (!(await formUtils.checkForm(formRef.value))) {
        loading.login = false
        return
      }

      if (!recaptchaVersionFailed.v3) {
        await v3RecaptchaVerify('loginByEmail')
      } else {
        if (!recaptchaVerifyToken.value) {
          showV2Recaptcha.value = true
          loading.login = false
          return
        }
      }

      const payload = {
        ...authFormData,
        recaptchaToken: recaptchaVerifyToken.value,
        recaptchaVersion: recaptchaVersionUse.value,
      }

      const [res, err, rawErr] = await EmailLogin(payload)
      loading.login = false
      if (err) {
        if (handleRecaptchaError(rawErr.errorCode)) return
        window.$message.error(err)
        return
      }

      if (res.token) {
        ls.setPendingLoginCredentials({
          orgCode: authFormData.orgCode,
          email: authFormData.email,
          password: authFormData.password,
        })
        store.commit('auth/SET_TOKEN', res.token)
        const { checkUnreadArticles } = useNewsHint()
        checkUnreadArticles()
      }

      window.$message.success('登入成功!')
      if (ls.getNeedTransfer()) {
        const transferPath = ls.getNeedTransfer()

        ls.removeNeedTransfer()
        router.push(transferPath)
      } else router.push('/').catch(() => {})
    }

    const onForgetPassword = () => {
      router.push({
        name: 'ForgetPassword',
        query: {
          email: authFormData.email,
        },
      })
    }

    onMounted(async () => {
      if (await checkSubDomain()) {
        return
      }
      const code = router.currentRoute.params.orgCode

      if (code) {
        authFormData.orgCode = code
        const org = orgList.value.find((item) => item.code === code)
        queryOrg.value = org?.name || ''
      }
    })
    return {
      formRef,
      iconOrg,
      iconEmail,
      iconPassword,
      gimmatekLogo,
      gimmatekLogo2x,
      subdomainCode,
      authFormData,
      orgList,
      queryOrg,
      selectedOrg,
      isClickTerms,
      formRules,
      version,
      changeOrg,
      getOrgsInfoByCode,
      checkSubDomain,
      handleLogin,
      handleClickTerms,
      handleChangeTerms,
      showV2Recaptcha,
      v2RecaptchaVerify,
      onCloseRecaptchaV2,
      loading,
      selectedLang,
      langs,
      onForgetPassword,
      showControl,
    }
  },
})
</script>
<style lang="scss" scoped>
.form-layout {
  @apply z-[1] relative flex flex-col justify-center items-center w-full px-[16px];

  .login-form {
    @apply mb-[16px] pt-[48px] px-[28px] pb-[18px] w-[600px] max-w-full bg-white;

    &__logo {
      @apply block h-[40px] mx-auto mb-[24px];
    }

    &__login {
      @apply relative flex-1 mb-[40px] text-center text-primary-100 text-[38px] font-bold tracking-[4px];
    }
  }

  .login-form__agreecheck {
    .agree-terms {
      @apply inline whitespace-normal text-primary-100 cursor-pointer;
    }
    ::v-deep {
      .el-checkbox {
        @apply flex whitespace-normal;
      }
      .el-checkbox__label {
        @apply text-info cursor-default;
      }
    }
  }

  .version {
    @apply absolute bottom-[68px] text-primary-50;
  }

  ::v-deep .el-form-item__content {
    @apply flex justify-center;
  }

  ::v-deep .el-button {
    @apply mt-[52px] w-full max-w-[400px] h-[60px] rounded-[34px] text-[24px];
  }

  ::v-deep .el-form-item__label {
    @apply relative w-full inline-flex text-primary-100;

    &::before {
      @apply absolute right-[-16px];
    }
  }

  ::v-deep .el-input__inner {
    @apply pb-[4px] pl-[48px] text-[20px] border-none rounded-none;
    border-bottom: 1px solid var(--primary-100);
  }

  ::v-deep .el-input__prefix {
    @apply hidden;
  }
}

.form__input__org,
.form__input__email,
.form__input__password {
  @apply w-full;
}

.prefix__icon {
  @apply absolute bottom-[8px] left-0 object-contain;
}

@media screen and (max-width: 1023px) {
  .form-layout {
    .login-form {
      @apply pt-[40px] px-[12px];
    }

    .version {
      @apply bottom-[16px] text-[14px] leading-[16px];
    }
  }
}

::v-deep .locale-changer .el-input {
  @apply w-[120px];
  .el-input__inner {
    @apply border-none;
  }
}
</style>
